import { motion } from "framer-motion";
import { Page } from "@/components/Page";
import { PageShell } from "@/components/PageShell";
import { EASE, Eyebrow, FadeUp } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { useUI } from "@/context/UIContext";
import { IMG } from "@/lib/images";

const DESKS = ["London", "Monaco", "Dubai", "New York", "Singapore"];

export default function About() {
  const { openEnquiry } = useUI();
  return (
    <Page testId="about-page">
      <PageShell
        testId="about-hero"
        eyebrow="The House"
        title={
          <>
            A house built on <span className="gold-text italic">discretion.</span>
          </>
        }
        intro="MDS Arabama was founded on a simple conviction: that true luxury is not possession, but access — quietly arranged, impeccably delivered."
        image={IMG.skyline}
        imageAlt="City skyline at dusk in warm haze"
      >
        <section className="mx-auto grid max-w-[1280px] gap-14 px-6 py-24 md:py-32 lg:grid-cols-2 lg:px-12">
          <div>
            <FadeUp>
              <Eyebrow>Our Story</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl font-medium leading-tight text-ivory sm:text-4xl">
                Old-world standards, modern reach
              </h2>
            </FadeUp>
          </div>
          <div className="space-y-6 text-sm font-light leading-relaxed text-ivory-muted">
            <FadeUp delay={0.15}>
              <p>
                From desks in Mayfair, Monte Carlo and Dubai, our directors serve a
                small register of families, founders and figures whose lives cross
                borders as a matter of course.
              </p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p>
                We do not advertise our successes and we do not discuss our
                clients. Our reputation travels the only way that matters — from
                one member to the next.
              </p>
            </FadeUp>
          </div>
        </section>

        <section className="grid lg:grid-cols-2">
          <div className="relative min-h-[380px] overflow-hidden lg:min-h-[520px]" data-cursor>
            <motion.img
              src={IMG.fleetSClass}
              alt="MDS Arabama chauffeur with the black Mercedes S-Class fleet"
              loading="lazy"
              initial={{ scale: 1.06, opacity: 0.6 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.4, ease: EASE }}
              className="img-lux-grade absolute inset-0 h-full w-full object-cover object-[75%_20%]"
            />
          </div>
          <div className="flex flex-col justify-center bg-[#080808] px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
            <FadeUp>
              <Eyebrow>Global Desks</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 max-w-md font-serif text-[32px] font-medium leading-[1.12] text-ivory sm:text-4xl">
                Wherever you are, <span className="gold-text italic">so are we.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <ul className="mt-8 space-y-4">
                {DESKS.map((d, i) => (
                  <li key={d} className="flex items-baseline gap-5 border-b border-[rgba(200,164,93,0.14)] pb-4">
                    <span className="gold-text font-serif text-lg italic">{`0${i + 1}`}</span>
                    <span className="font-serif text-2xl font-medium text-ivory">{d}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.3} className="mt-10">
              <LuxButton variant="outline" onClick={() => openEnquiry("Concierge")} testId="about-enquire-button">
                Enquire
              </LuxButton>
            </FadeUp>
          </div>
        </section>
      </PageShell>
    </Page>
  );
}
